import { DeviceConfigurationTemplateClient } from "@formant/core/client/DeviceConfigurationTemplateClient";
import { defined } from "@formant/core/common/defined";
import { DefinedEntity } from "@formant/core/model/DefinedEntity";
import { IDeviceConfigurationTemplate } from "@formant/core/model/IDeviceConfigurationTemplate";
import { Uuid } from "@formant/core/model/Uuid";

import { IAuthStore } from "./IAuthStore";
import { IDeviceConfigurationTemplateStore } from "./IDeviceConfigurationTemplateStore";
import { StoreCache } from "./StoreCache";

export class DeviceConfigurationTemplateStore implements IDeviceConfigurationTemplateStore {
    private listCache = new StoreCache<
        undefined,
        DefinedEntity<IDeviceConfigurationTemplate>[]
    >();
    private cache = new StoreCache<
        Uuid,
        DefinedEntity<IDeviceConfigurationTemplate> | false
    >();

    constructor(
        private deviceConfigurationTemplateClient: DeviceConfigurationTemplateClient,
        private authStore: IAuthStore,
    ) {}

    public list(): DefinedEntity<IDeviceConfigurationTemplate>[] | undefined {
        return this.listAll()?.filter(_ => _.enabled);
    }

    public listAll():
        | DefinedEntity<IDeviceConfigurationTemplate>[]
        | undefined {
        return this.listCache.get(
            undefined,
            async () => await this.fetchList(),
        );
    }

    public get(
        id: Uuid,
    ): DefinedEntity<IDeviceConfigurationTemplate> | false | undefined {
        return this.cache.get(
            id,
            async () => (await this.fetchGet(id)) || false,
        );
    }

    public async create(
        deviceConfigurationTemplate: IDeviceConfigurationTemplate,
    ): Promise<DefinedEntity<IDeviceConfigurationTemplate>> {
        const created = await this.deviceConfigurationTemplateClient.create(
            await this.authStore.token(),
            deviceConfigurationTemplate,
        );
        this.cache.set(defined(created.id), created);
        this.listCache.clear();
        return created;
    }

    public async update(
        id: Uuid,
        deviceConfigurationTemplate: Partial<IDeviceConfigurationTemplate>,
    ): Promise<DefinedEntity<IDeviceConfigurationTemplate>> {
        const updated = await this.deviceConfigurationTemplateClient.update(
            await this.authStore.token(),
            id,
            deviceConfigurationTemplate,
        );
        this.cache.set(id, updated);
        this.listCache.clear();
        return updated;
    }

    public save(
        deviceConfigurationTemplate: IDeviceConfigurationTemplate,
    ): Promise<DefinedEntity<IDeviceConfigurationTemplate>> {
        const { id } = deviceConfigurationTemplate;
        if (id) {
            return this.update(id, deviceConfigurationTemplate);
        } else {
            return this.create(deviceConfigurationTemplate);
        }
    }

    public async delete(id: Uuid): Promise<void> {
        await this.deviceConfigurationTemplateClient.delete(
            await this.authStore.token(),
            id,
        );
        this.cache.set(id, false);
        this.listCache.clear();
    }

    public async disable(
        id: Uuid,
    ): Promise<DefinedEntity<IDeviceConfigurationTemplate>> {
        return await this.update(id, { enabled: false });
    }

    private async fetchGet(
        id: Uuid,
    ): Promise<DefinedEntity<IDeviceConfigurationTemplate> | false> {
        const deviceConfigurationTemplate =
            await this.deviceConfigurationTemplateClient.get(
                await this.authStore.token(),
                id,
            );
        const value = deviceConfigurationTemplate || false;
        this.cache.set(id, value);
        return value;
    }

    private async fetchList(): Promise<
        DefinedEntity<IDeviceConfigurationTemplate>[]
    > {
        const value = await this.deviceConfigurationTemplateClient.list(
            await this.authStore.token(),
        );
        value.forEach(_ => this.cache.set(defined(_.id), _));
        this.listCache.set(undefined, value);
        return value;
    }
}
